import React, { useEffect, useState } from 'react';

const Loading = ({ 
  sposo = "Gianluca", 
  sposa = "Pamela", 
  fotoUrl = null,
  duration = 3000,
  onComplete = null 
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        clearInterval(interval);
        if (onComplete) {
          setTimeout(onComplete, 200);
        }
      }
    }, 16);

    return () => clearInterval(interval);
  }, [duration, onComplete]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Josefin+Sans:wght@200;300&display=swap');

        .wedding-loader {
          /* Nuova palette */
          --dusty-blue: #c3e5ea;
          --dusty-blue-light: #e8ebe6;
          --dusty-blue-dark: #a8d5dd;
          --dusty-blue-deeper: #8ac4d0;
          --cream: #ffffff;
          --cream-warm: #fdfcfb;
          --gold: #f6cdca;
          --gold-light: #f6cdca;
          --text-dark: #3a4a5c;
          --text-soft: #5a6a7c;

          font-family: 'Josefin Sans', sans-serif;
          background: var(--cream-warm);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
        }

        .wedding-loader * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .bg-gradient {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background:
            radial-gradient(ellipse at 30% 20%, rgba(195, 229, 234, 0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(246, 205, 202, 0.15) 0%, transparent 50%),
            var(--cream-warm);
          animation: bgPulse 4s ease-in-out infinite;
        }

        @keyframes bgPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        .loader-content {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          z-index: 10;
        }

        .photo-wrapper {
          position: relative;
          width: 280px;
          height: 280px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .rotating-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 1px solid transparent;
          border-top-color: var(--gold);
          border-right-color: var(--gold-light);
          animation: rotate 3s linear infinite;
        }

        .rotating-ring::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          border-radius: 50%;
          border: 1px solid transparent;
          border-bottom-color: var(--dusty-blue);
          border-left-color: var(--dusty-blue-light);
          animation: rotate 2s linear infinite reverse;
        }

        @keyframes rotate {
          to { transform: rotate(360deg); }
        }

        .pulse-ring {
          position: absolute;
          width: 260px;
          height: 260px;
          border-radius: 50%;
          border: 1px solid var(--dusty-blue-light);
          animation: pulse 2s ease-in-out infinite;
        }

        .pulse-ring:nth-of-type(2) { animation-delay: 0.5s; }
        .pulse-ring:nth-of-type(3) { animation-delay: 1s; }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.3); opacity: 0; }
        }

        .photo-frame {
          position: relative;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          padding: 5px;
          background: linear-gradient(135deg, var(--gold) 0%, #f4beb8 50%, var(--gold) 100%);
          box-shadow:
            0 15px 40px rgba(246, 205, 202, 0.5),
            0 5px 20px rgba(0, 0, 0, 0.1);
          animation: photoFloat 3s ease-in-out infinite;
        }

        @keyframes photoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        .photo-inner {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: linear-gradient(145deg, var(--dusty-blue-light) 0%, var(--dusty-blue) 100%);
          border: 3px solid var(--cream);
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .photo-inner img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .photo-placeholder {
          color: var(--cream);
          opacity: 0.9;
        }

        .tulip {
          position: absolute;
          z-index: 5;
        }

        .tulip-left-1 { left: -70px; top: 30%; animation: tulipSway 3s ease-in-out infinite; }
        .tulip-left-2 { left: -45px; top: 55%; animation: tulipSway 3.5s ease-in-out infinite 0.3s; }
        .tulip-left-3 { left: -55px; top: 15%; animation: tulipSway 2.8s ease-in-out infinite 0.6s; }
        .tulip-right-1 { right: -70px; top: 30%; animation: tulipSwayReverse 3s ease-in-out infinite; }
        .tulip-right-2 { right: -45px; top: 55%; animation: tulipSwayReverse 3.5s ease-in-out infinite 0.3s; }
        .tulip-right-3 { right: -55px; top: 15%; animation: tulipSwayReverse 2.8s ease-in-out infinite 0.6s; }

        @keyframes tulipSway {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }

        @keyframes tulipSwayReverse {
          0%, 100% { transform: rotate(3deg); }
          50% { transform: rotate(-3deg); }
        }

        .petals {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
          z-index: 1;
        }

        .petal {
          position: absolute;
          width: 12px;
          height: 18px;
          background: var(--dusty-blue);
          border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
          opacity: 0;
          animation: petalFall linear infinite;
        }

        .petal:nth-of-type(1) { left: 10%; animation-duration: 8s; background: var(--gold); width: 11px; height: 16px; }
        .petal:nth-of-type(2) { left: 20%; animation-duration: 10s; animation-delay: 1s; background: var(--dusty-blue); }
        .petal:nth-of-type(3) { left: 35%; animation-duration: 9s; animation-delay: 2s; background: var(--dusty-blue-light); width: 13px; height: 17px; }
        .petal:nth-of-type(4) { left: 50%; animation-duration: 11s; animation-delay: 0.5s; background: var(--gold); width: 10px; height: 14px; }
        .petal:nth-of-type(5) { left: 65%; animation-duration: 8.5s; animation-delay: 3s; background: var(--dusty-blue); width: 12px; height: 18px; }
        .petal:nth-of-type(6) { left: 80%; animation-duration: 10s; animation-delay: 1.5s; background: var(--gold-light); width: 11px; height: 15px; }
        .petal:nth-of-type(7) { left: 90%; animation-duration: 9.5s; animation-delay: 2.5s; background: var(--dusty-blue-light); }

        @keyframes petalFall {
          0% { top: -20px; opacity: 0; transform: rotate(0deg) translateX(0); }
          10% { opacity: 0.7; }
          90% { opacity: 0.7; }
          100% { top: 100vh; opacity: 0; transform: rotate(360deg) translateX(50px); }
        }

        .text-content {
          margin-top: 50px;
          text-align: center;
        }

        .names {
          font-family: 'Cormorant Garamond', serif;
          font-size: 42px;
          font-weight: 300;
          font-style: italic;
          color: var(--dusty-blue-deeper);
          letter-spacing: 2px;
          animation: fadeInUp 1s ease-out;
        }

        .ampersand {
          color: var(--gold);
          font-style: normal;
          margin: 0 8px;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Central Progress Bar */
        .progress-section {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 40px;
          animation: fadeInUp 1s ease-out 0.3s both;
        }

        .progress-container {
          width: 320px;
          max-width: 80vw;
          height: 3px;
          background: var(--dusty-blue-light);
          border-radius: 3px;
          overflow: hidden;
          position: relative;
        }

        .progress-container::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          animation: shimmer 2s infinite;
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, var(--dusty-blue-dark), var(--gold), var(--dusty-blue-dark));
          background-size: 200% 100%;
          border-radius: 3px;
          transition: width 0.1s linear;
          animation: gradientMove 2s linear infinite;
        }

        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }

        .progress-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 320px;
          max-width: 80vw;
          margin-top: 15px;
        }

        .loading-text {
          font-size: 11px;
          font-weight: 200;
          letter-spacing: 6px;
          text-transform: uppercase;
          color: var(--dusty-blue-dark);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .loading-dots {
          display: flex;
          gap: 4px;
        }

        .loading-dots span {
          width: 4px;
          height: 4px;
          background: var(--gold);
          border-radius: 50%;
          animation: dotPulse 1.4s ease-in-out infinite;
        }

        .loading-dots span:nth-of-type(2) { animation-delay: 0.2s; }
        .loading-dots span:nth-of-type(3) { animation-delay: 0.4s; }

        @keyframes dotPulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }

        .progress-percentage {
          font-family: 'Cormorant Garamond', serif;
          font-size: 14px;
          color: var(--gold);
          font-weight: 400;
          letter-spacing: 1px;
        }

        .corner-decor {
          position: absolute;
          width: 80px;
          height: 80px;
          opacity: 0.5;
        }

        .corner-decor.top-left { top: 30px; left: 30px; }
        .corner-decor.top-right { top: 30px; right: 30px; transform: scaleX(-1); }
        .corner-decor.bottom-left { bottom: 30px; left: 30px; transform: scaleY(-1); }
        .corner-decor.bottom-right { bottom: 30px; right: 30px; transform: scale(-1); }

        @media (max-width: 480px) {
          .photo-wrapper { width: 240px; height: 240px; }
          .photo-frame { width: 160px; height: 160px; }
          .pulse-ring { width: 220px; height: 220px; }
          .tulip-left-1, .tulip-left-2, .tulip-left-3 { left: -40px; transform: scale(0.7); }
          .tulip-right-1, .tulip-right-2, .tulip-right-3 { right: -40px; transform: scale(0.7); }
          .names { font-size: 32px; }
          .corner-decor { width: 50px; height: 50px; }
          .progress-container, .progress-info { width: 260px; }
        }
      `}</style>

      <div className="wedding-loader">
        <div className="bg-gradient" />

        {/* Floating petals */}
        <div className="petals">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="petal" />
          ))}
        </div>

        {/* Corner decorations */}
        {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((pos) => (
          <div key={pos} className={`corner-decor ${pos}`}>
            <svg viewBox="0 0 100 100" fill="none">
              <path d="M10 90 L10 30 Q10 10 30 10 L90 10" stroke="#c3e5ea" strokeWidth="1" />
              <circle cx="25" cy="25" r="4" fill="#f6cdca" />
              <path d="M10 50 Q25 45 30 30" stroke="#c3e5ea" strokeWidth="1" opacity="0.5" />
            </svg>
          </div>
        ))}

        <div className="loader-content">
          <div className="photo-wrapper">
            {/* Pulse rings */}
            {[...Array(3)].map((_, i) => (
              <div key={i} className="pulse-ring" />
            ))}

            {/* Rotating ring */}
            <div className="rotating-ring" />

            {/* Tulips Left */}
            <div className="tulip tulip-left-1">
              <svg width="50" height="90" viewBox="0 0 50 90">
                <path d="M25 90 L25 45" stroke="#c3e5ea" strokeWidth="2" />
                <path d="M25 50 Q10 35 15 15 Q20 0 25 5 Q30 0 35 5 Q40 0 40 15 Q45 35 25 50" fill="#f6cdca" />
                <path d="M25 50 L25 20" stroke="#c3e5ea" strokeWidth="1" opacity="0.4" />
              </svg>
            </div>
            <div className="tulip tulip-left-2">
              <svg width="45" height="80" viewBox="0 0 45 80">
                <path d="M22 80 L22 40" stroke="#c3e5ea" strokeWidth="2" />
                <path d="M22 45 Q8 30 12 12 Q17 0 22 4 Q27 0 32 4 Q37 0 35 12 Q40 30 22 45" fill="#e8ebe6" />
                <path d="M22 45 L22 18" stroke="#c3e5ea" strokeWidth="1" opacity="0.4" />
                <path d="M24 60 Q38 55 42 40" stroke="#c3e5ea" strokeWidth="1.5" fill="none" />
              </svg>
            </div>
            <div className="tulip tulip-left-3">
              <svg width="40" height="70" viewBox="0 0 40 70">
                <path d="M20 70 L20 35" stroke="#c3e5ea" strokeWidth="1.5" />
                <path d="M20 38 Q8 26 11 10 Q15 0 20 3 Q25 0 29 3 Q33 0 30 10 Q35 26 20 38" fill="#f6cdca" opacity="0.8" />
              </svg>
            </div>

            {/* Tulips Right */}
            <div className="tulip tulip-right-1">
              <svg width="50" height="90" viewBox="0 0 50 90">
                <path d="M25 90 L25 45" stroke="#c3e5ea" strokeWidth="2" />
                <path d="M25 50 Q10 35 15 15 Q20 0 25 5 Q30 0 35 5 Q40 0 40 15 Q45 35 25 50" fill="#f6cdca" />
                <path d="M25 50 L25 20" stroke="#c3e5ea" strokeWidth="1" opacity="0.4" />
              </svg>
            </div>
            <div className="tulip tulip-right-2">
              <svg width="45" height="80" viewBox="0 0 45 80">
                <path d="M22 80 L22 40" stroke="#c3e5ea" strokeWidth="2" />
                <path d="M22 45 Q8 30 12 12 Q17 0 22 4 Q27 0 32 4 Q37 0 35 12 Q40 30 22 45" fill="#e8ebe6" />
                <path d="M22 45 L22 18" stroke="#c3e5ea" strokeWidth="1" opacity="0.4" />
                <path d="M24 60 Q38 55 42 40" stroke="#c3e5ea" strokeWidth="1.5" fill="none" />
              </svg>
            </div>
            <div className="tulip tulip-right-3">
              <svg width="40" height="70" viewBox="0 0 40 70">
                <path d="M20 70 L20 35" stroke="#c3e5ea" strokeWidth="1.5" />
                <path d="M20 38 Q8 26 11 10 Q15 0 20 3 Q25 0 29 3 Q33 0 30 10 Q35 26 20 38" fill="#f6cdca" opacity="0.8" />
              </svg>
            </div>

            {/* Photo frame */}
            <div className="photo-frame">
              <div className="photo-inner">
                {fotoUrl ? (
                  <img src={fotoUrl} alt="Gli sposi" />
                ) : (
                  <div className="photo-placeholder">
                    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text-content">
            <h1 className="names">
              {sposa} <span className="ampersand">&</span> {sposo}
            </h1>

            {/* Central Progress Section */}
            <div className="progress-section">
              <div className="progress-container">
                <div 
                  className="progress-bar" 
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="progress-info">
                <p className="loading-text">
                  Caricamento
                  <span className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </p>
                <span className="progress-percentage">{Math.round(progress)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Loading;